import React from 'react';
import PropTypes from 'prop-types';
import i18n from '~shared/utils/i18n';
import { Link } from '../../../../spa/Components/Link/Link';
import { nextRepaint } from './../../../utils/dom';
import isUndefined from 'lodash/isUndefined';
import { alterMarketName } from '~shared/utils/market-utils';
import { seoUrlSport, slugifyUrlEntities } from '~spa/Utils/SeoUrls';
import { getSportIdBySlug } from '~shared/utils/sportsbook';
import { ODDS_BOOST_SLUG } from '~shared/utils/constants';

import createSportSelectionView from './../../sport-selection-view/createSportSelectionView';

import {
    BB_SPORT_SELECTION_CONTAINER_CSS_CLASS_BLOCK_NAME,
    BB_SPORT_BETFEED_CSS_CLASS_BLOCK_NAME,
    BB_SPORT_BETFEED_PRICE_CSS_CLASS,
} from './constants';

import './style.scss';
import { SELECTION_CSS_CLASS_BLOCK_NAME, GTM_ACTION_BET_CARD } from '../../sport-selection-view/constants';
import { seoUrlEvent, seoUrlInplayEvent, seoUrlTournamentOutrights } from '../../../../spa/Utils/SeoUrls';


class SportsSelectionViewContainer extends React.Component {

    constructor(props) {
        super(props);
        this.state = isUndefined(this.state) && {};
        this.selection = null;
        this.selectionContainerEl = null;
        this.selectionButtonEl = null;
        this.event_scheduled_start = null;
        this.market_betable = null;
        this.event_betable = null;
        this.state = {
            isError: false,
            error: undefined,
        };
    }

    static propTypes = {
        commonDataEvents: PropTypes.object.isRequired,
        selection: PropTypes.object,
        displaySelectionNameOdds: PropTypes.bool,
        errorHandler: PropTypes.func,
        gtmAttribute: PropTypes.string,
        linkClick: PropTypes.func,
    };

    static defaultProps = {
        gtmAttribute: '',
        linkClick: () => null,
    }

    isSelectionBetable() {
        const selection = this.props.selection;
        const event = this.props.commonDataEvents[this.props.selection.event_id];
        const market = event.markets[this.props.selection.market_id];

        return (!isUndefined(selection.betable) ? selection.betable : !selection.suspended)
            && (!isUndefined(market.betable) ? market.betable : !market.suspended)
            && (!isUndefined(event.betable) ? event.betable : !event.suspended);
    }

    /**
     * Prepare an object to pass to the createSportsSelectionView function
     * @param {*} param0 object with required params from redux common store
     * @todo Move into a shared function for shared redux store...
     *
     * @returns {object} object compatable with createSportsSelectionView
     */
    prepSportsSelectionViewData({ selection, event, market }) {
        const betable = this.isSelectionBetable();

        const {
            home_team_name: homeTeamName,
            away_team_name: awayTeamName,
        } = selection;
        const { sport_slug: sportSlug } = event;
        const { name: marketName } = market;
        const newMarketName = alterMarketName(marketName, homeTeamName, awayTeamName, sportSlug);

        let selectionData = {
            id: selection.id,
            selection_name: selection.name,
            sport: sportSlug,
            is_inplay: selection.is_inplay,
            sp_only: (selection.decimal_price === null || selection.sp_only),
            suspended: !betable,
            event_id: event.id,
            event_name: event.name,
            market_id: market.id,
            market_name: newMarketName,
            scheduled_start: event.scheduled_start,
            decimal_price: selection.decimal_price,
            fraction_price: selection.fraction_price,
            american_price: selection.american_price,
            counter: null,
            gtmAttribute: `${this.props.gtmAttribute};`,
            isOutright: event.event_type?.toLowerCase() === 'outright' || false,
            tournamentName: event.tournament_name,
            category: event.category_slug,
            team: selection.team_slug,
            tournamentLogos: event.tournament_logos,
        };
        if (selection.hasOwnProperty('extra_info')) {
            selectionData.extra_info = selection.extra_info;
        }

        return selectionData;
    }

    componentDidMount() {
        this.initializeSportSelectionView();
    }

    componentWillUnmount() {
        this.selection.reactUnmountCallbacks.forEach(cb => cb());
    }

    componentDidUpdate(prevProps) {
        if (!this.state.isError) {
            try {
                let newState = {};
                if (isUndefined(this.props.selection)) {
                    newState = { suspended: true };
                } else {
                    const eventId = this.props.selection.event_id;
                    const marketId = this.props.selection.market_id;
                    const event = this.props.commonDataEvents[eventId];
                    const eventBetable = isUndefined(event.betable) || event.betable;
                    const market = event.markets[marketId];
                    const marketBetable = isUndefined(market.betable) || market.betable;

                    const betable = this.props.selection.betable && marketBetable && eventBetable;

                    if (
                        JSON.stringify(this.props.selection) !== JSON.stringify((prevProps.selection))
                        || this.event_scheduled_start !== this.props.commonDataEvents[eventId].scheduled_start
                        || this.market_betable !== marketBetable
                        || this.event_betable !== eventBetable
                    ) {
                        this.event_scheduled_start = this.props.commonDataEvents[eventId].scheduled_start;
                        this.market_betable = marketBetable;
                        this.event_betable = eventBetable;

                        newState = {
                            suspended: !betable,
                            decimal_price: this.props.selection.decimal_price,
                            fraction_price: this.props.selection.fraction_price,
                            american_price: this.props.selection.american_price,
                            sp_only: this.props.selection.sp_only,
                            scheduled_start: this.event_scheduled_start,
                        };
                    }
                }
            this.selection.update(newState);
        } catch (err) {
            this.handlerError(err);
        }
    }
}

    /**
     * Creates an a element with the event url as a target. Creates a SportsSelectionView
     * and appends it as a child of the A element.
     * Appends this A element to the selectionContainer element.
     * @returns {void}
     */
    initializeSportSelectionView() {
        const selection = this.props.selection;

        try {
            const event = this.props.commonDataEvents[this.props.selection.event_id];
            const market = event.markets[this.props.selection.market_id];
            //store event scheduled time so we can check for changes.
            this.event_scheduled_start = event.scheduled_start;
            const selectionData = this.prepSportsSelectionViewData({selection, event, market});
            if (!isUndefined(this.props.displaySelectionNameOdds)) {
                selectionData.displaySelectionNameOdds = true;
            }
            this.selection = createSportSelectionView(selectionData);

        } catch (err) {
            this.handlerError(err);
        }
        nextRepaint(() => {
            try {
                this.selectionContainerEl.appendChild(this.selection.el);
                this.selectionButtonEl.appendChild(this.selection.selectionView.el);
            } catch (err) {
                this.handlerError(err);
            }
        });
    }

    handlerError(err) {
        this.setState({
            isError: true,
            error: err,
        });
        if (!isUndefined(this.props.errorHandler)) {
            this.props.errorHandler(err);
        }
    }

    stopSelectionClickPropagation(e) {
        if (!isUndefined(e.target.classList) && e.target.classList.contains(SELECTION_CSS_CLASS_BLOCK_NAME)) {
            e.stopPropagation();
        }
    }

    render() {
        let event_url = '';
        const { gtmAttribute } = this.props;
        const oddsBoostLink = slugifyUrlEntities(i18n.t('specials.title'));

        if (!isUndefined(this.props.selection) && !isUndefined(this.props.commonDataEvents[this.props.selection.event_id])) {
            const { event_id: eventId } = this.props.selection;
            const { event_type: eventType = '',
                event_url: eventUrl,
                tournament_id: tournamentId,
                sport_slug: sportSlug,
            } = this.props.commonDataEvents[eventId];

            if (sportSlug === ODDS_BOOST_SLUG) {
                // direct to odds-boost page instead of sport page if it's odds-boost card.
                event_url = seoUrlSport(oddsBoostLink, getSportIdBySlug(sportSlug));
            } else if (eventType.toLowerCase() === 'outright') {
                event_url = seoUrlTournamentOutrights(eventUrl, tournamentId);
            } else if (this.props.selection.is_inplay) {
                event_url = seoUrlInplayEvent(eventUrl, eventId);
            } else {
                event_url = seoUrlEvent(eventUrl, eventId);
            }
        }

        return (
            <div className={BB_SPORT_BETFEED_CSS_CLASS_BLOCK_NAME}>
                <Link
                    to={event_url}
                    className={BB_SPORT_SELECTION_CONTAINER_CSS_CLASS_BLOCK_NAME}
                    gtmAttribute={`${gtmAttribute};${GTM_ACTION_BET_CARD}`}
                    onClick={this.props.linkClick}
                >
                    <div ref={el => { this.selectionContainerEl = el; }} onClick={this.stopSelectionClickPropagation} />
                </Link>
                <span className={BB_SPORT_BETFEED_PRICE_CSS_CLASS} ref={el => { this.selectionButtonEl = el; }} />
            </div>
        );
    }
}

export default SportsSelectionViewContainer;
